import type { NextApiRequest, NextApiResponse } from 'next';

import { LOGGER_SEVERITY, createLoggers } from './logger';
import { isPlainObject, redactPii } from './redact-pii';

const MAX_MESSAGE_LENGTH = 200;
const MAX_BODY_BYTES = 4096;

const ALLOWED_SEVERITIES = new Set<string>([LOGGER_SEVERITY.WARN, LOGGER_SEVERITY.ERROR]);

interface ClientLogPayload {
  severity: typeof LOGGER_SEVERITY.WARN | typeof LOGGER_SEVERITY.ERROR;
  tag: string;
  message: string;
  payload?: Record<string, unknown>;
}

function parseClientLogPayload(body: unknown): ClientLogPayload | null {
  if (!body || typeof body !== 'object') return null;

  const { severity, tag, message, payload } = body as Record<string, unknown>;

  if (typeof severity !== 'string' || !ALLOWED_SEVERITIES.has(severity)) {
    return null;
  }

  if (typeof tag !== 'string' || tag.length === 0 || tag.length > 100) {
    return null;
  }

  if (typeof message !== 'string' || message.length === 0) {
    return null;
  }

  if (payload !== undefined && !isPlainObject(payload)) {
    return null;
  }

  return {
    severity: severity as ClientLogPayload['severity'],
    tag,
    message: message.slice(0, MAX_MESSAGE_LENGTH),
    ...(payload ? { payload } : {}),
  };
}

export function createPagesApiClientLogHandler() {
  return async function clientLogHandler(req: NextApiRequest, res: NextApiResponse) {
    if (req.method !== 'POST') {
      res.setHeader('Allow', 'POST');
      res.status(405).end();
      return;
    }

    const contentLength = Number(req.headers['content-length'] ?? 0);
    if (contentLength > MAX_BODY_BYTES) {
      res.status(413).end();
      return;
    }

    const parsed = parseClientLogPayload(req.body);
    if (!parsed) {
      res.status(400).end();
      return;
    }

    const { warn, error } = createLoggers({ tag: parsed.tag });
    const safePayload = parsed.payload ? redactPii(parsed.payload) : {};
    const logPayload = { ...safePayload, source: 'browser' };

    if (parsed.severity === LOGGER_SEVERITY.ERROR) {
      error(parsed.message, logPayload);
    } else {
      warn(parsed.message, logPayload);
    }

    res.status(204).end();
  };
}
